import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Send } from "lucide-react";
import { useSiteContext } from "../../../../context/SiteContext";
import requests from "../../../../services/httpServices";

const formatDate = (value) => {
  if (!value) return "";
  try {
    return new Date(value).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
  } catch { return ""; }
};

export default function MemberSupportTicket() {
  const { user } = useSiteContext();
  const navigate = useNavigate();
  const { ticketId } = useParams();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const memberId = user?.memberId;

  useEffect(() => {
    if (!memberId || !ticketId) return;
    let isMounted = true;
    const loadTicket = async () => {
      setLoading(true);
      setError("");
      try {
        const data = await requests.get(`/member/${memberId}/support-tickets/${ticketId}`);
        if (data?.success === false) throw new Error(data?.message || "Failed to load ticket.");
        if (isMounted) setTicket(data?.ticket || null);
      } catch (err) {
        if (isMounted) setError(err?.message || "Failed to load ticket.");
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    loadTicket();
    return () => { isMounted = false; };
  }, [memberId, ticketId]);

  const statusLabel = ticket?.status === "resolved"
    ? "Resolved"
    : ticket?.messages?.some((msg) => msg?.role === "admin")
    ? "Responded"
    : "Open";

  const handleSendReply = async () => {
    if (!reply.trim() || !memberId || !ticketId) return;
    setSending(true);
    try {
      const data = await requests.post(`/member/${memberId}/support-tickets/${ticketId}/reply`, { message: reply.trim() });
      if (data?.success === false) throw new Error(data?.message || "Failed to send reply.");
      const updated = data?.ticket || {};
      setTicket((prev) => ({ ...(prev || {}), ...updated, messages: updated?.messages || prev?.messages || [], status: updated?.status || prev?.status }));
      setReply("");
    } catch (err) {
      setError(err?.message || "Failed to send reply.");
    } finally {
      setSending(false);
    }
  };

  if (!memberId) return <div style={{ padding: 40, color: "rgba(255,255,255,0.4)" }}>Member account required.</div>;

  return (
    <div style={{ padding: "8px 16px 40px", fontFamily: "'DM Sans', sans-serif", color: "#ffffff" }}>
      <button
        onClick={() => navigate("/dashboard/support")}
        style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "transparent", border: "none", color: "rgba(255,255,255,0.6)", fontSize: 14, cursor: "pointer", marginBottom: 20 }}
      >
        <ArrowLeft size={16} /> Back to Support
      </button>

      <div style={{ padding: 24, borderRadius: 14, background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)" }}>
        {loading ? (
          <div style={{ fontSize: 14, color: "rgba(255,255,255,0.6)" }}>Loading ticket...</div>
        ) : error ? (
          <div style={{ fontSize: 14, color: "#ef4444" }}>{error}</div>
        ) : !ticket ? (
          <div style={{ fontSize: 14, color: "rgba(255,255,255,0.6)" }}>Ticket not found.</div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", color: "rgba(255,255,255,0.4)" }}>Support Ticket</div>
              <h1 style={{ fontSize: 24, fontWeight: 800, margin: "8px 0", color: "#ffffff" }}>{ticket.subject}</h1>
              <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
                <span style={{ fontSize: 12, padding: "4px 10px", borderRadius: 6, background: "rgba(106,31,191,0.15)", color: "#a64dff", fontWeight: 600 }}>{ticket.category || "General"}</span>
                <span style={{ fontSize: 12, padding: "4px 10px", borderRadius: 6, background: "rgba(250,204,21,0.15)", color: "#f59e0b", fontWeight: 600 }}>{statusLabel}</span>
                <span style={{ fontSize: 12, color: "rgba(255,255,255,0.5)" }}>{formatDate(ticket.created_at)}</span>
              </div>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {(ticket.messages || []).map((msg, idx) => {
                const isMember = msg?.role === "member";
                return (
                  <div key={`${msg?.createdAt || idx}-${idx}`} style={{ display: "flex", justifyContent: isMember ? "flex-end" : "flex-start" }}>
                    <div style={{
                      maxWidth: "70%", padding: "12px 14px", borderRadius: 12,
                      background: isMember ? "linear-gradient(135deg, rgba(106,31,191,0.9), rgba(166,77,255,0.9))" : "rgba(255,255,255,0.06)",
                      color: isMember ? "#ffffff" : "rgba(255,255,255,0.9)",
                      fontSize: 14, lineHeight: 1.6,
                    }}>
                      <div>{msg?.body}</div>
                      <div style={{ marginTop: 6, fontSize: 11, color: isMember ? "rgba(255,255,255,0.7)" : "rgba(255,255,255,0.4)" }}>
                        {formatDate(msg?.createdAt)}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 12, paddingTop: 8 }}>
              <textarea
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                placeholder="Write a reply..."
                rows={4}
                style={{ width: "100%", padding: "12px 14px", borderRadius: 10, border: "1px solid rgba(255,255,255,0.1)", background: "rgba(255,255,255,0.03)", color: "#ffffff", fontSize: 14, fontFamily: "'DM Sans', sans-serif", resize: "vertical", boxSizing: "border-box" }}
              />
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <button
                  onClick={handleSendReply}
                  disabled={sending || !reply.trim()}
                  style={{
                    display: "inline-flex", alignItems: "center", gap: 8, padding: "10px 18px", borderRadius: 10, border: "none",
                    background: "linear-gradient(135deg, #6a1fbf, #a64dff)", color: "#ffffff",
                    fontSize: 14, fontWeight: 700,
                    cursor: sending || !reply.trim() ? "not-allowed" : "pointer",
                    opacity: sending || !reply.trim() ? 0.6 : 1,
                  }}
                >
                  <Send size={16} /> {sending ? "Sending..." : "Send Reply"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
